import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert, Tabs, Tab } from "react-bootstrap";
import {
  MerchantStatusType,
  IMerchantSimple,
  checkoutTechnologyType,
} from "../../interfaces";
import { useApi } from "../../hooks";

interface MerchantDownloadModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  selectedMerchants?: IMerchantSimple[];
}

export const MerchantDownloadModal: React.FC<MerchantDownloadModalProps> = ({
  show,
  onHide,
  onSuccess,
  selectedMerchants = [],
}) => {
  const { get } = useApi();
  const [activeTab, setActiveTab] = useState<"selected" | "filtered">(
    selectedMerchants.length > 0 ? "selected" : "filtered"
  );
  const [selectedStatuses, setSelectedStatuses] = useState<
    MerchantStatusType[]
  >(
    Object.values(MerchantStatusType).filter(
      (status) => status !== MerchantStatusType.NEW
    )
  );
  const [selectedCheckoutTypes, setSelectedCheckoutTypes] = useState<
    checkoutTechnologyType[]
  >(Object.values(checkoutTechnologyType));
  const [createdAtFrom, setCreatedAtFrom] = useState<string>("");
  const [createdAtTo, setCreatedAtTo] = useState<string>("");
  const [updatedAtFrom, setUpdatedAtFrom] = useState<string>("");
  const [updatedAtTo, setUpdatedAtTo] = useState<string>("");
  const [lastCouponCountFilter, setLastCouponCountFilter] = useState<
    "all" | "withCoupons" | "noCoupons"
  >("all");
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update activeTab when selectedMerchants changes
  useEffect(() => {
    if (show) {
      setActiveTab(selectedMerchants.length > 0 ? "selected" : "filtered");
    }
  }, [selectedMerchants, show]);

  const handleStatusChange = (status: MerchantStatusType) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleCheckoutTypeChange = (checkoutType: checkoutTechnologyType) => {
    setSelectedCheckoutTypes((prev) =>
      prev.includes(checkoutType)
        ? prev.filter((ct) => ct !== checkoutType)
        : [...prev, checkoutType]
    );
  };

  const handleSelectAll = () => {
    setSelectedStatuses(Object.values(MerchantStatusType));
  };

  const handleDeselectAll = () => {
    setSelectedStatuses([]);
  };

  const handleSelectAllCheckoutTypes = () => {
    setSelectedCheckoutTypes(Object.values(checkoutTechnologyType));
  };

  const handleDeselectAllCheckoutTypes = () => {
    setSelectedCheckoutTypes([]);
  };

  const setUpdatedInLastHours = (hours: number) => {
    const now = new Date();
    const from = new Date(now.getTime() - hours * 60 * 60 * 1000);

    const toDatetimeLocal = (date: Date) => {
      const pad = (num: number) => String(num).padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    console.log(toDatetimeLocal(from), toDatetimeLocal(now));

    setUpdatedAtFrom(toDatetimeLocal(from));
    setUpdatedAtTo(toDatetimeLocal(now));
  };

  const clearDateFilters = () => {
    setCreatedAtFrom("");
    setCreatedAtTo("");
    setUpdatedAtFrom("");
    setUpdatedAtTo("");
  };

  // Convert datetime-local string to UTC ISO string for API
  const convertToUtcIso = (dateTimeLocal: string): string | undefined => {
    if (!dateTimeLocal) return undefined;

    // datetime-local format is "YYYY-MM-DDTHH:mm"
    // We need to interpret this as local time, then convert to UTC
    const localDate = new Date(dateTimeLocal + ":00"); // Add :00 for seconds
    return localDate.toISOString();
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      setError(null);

      let data: any;

      if (activeTab === "selected") {
        // Download selected merchants through the server
        if (selectedMerchants.length === 0) {
          setError("No merchants selected");
          setIsDownloading(false);
          return;
        }

        // Extract merchant IDs and request their cleaned DTOs from the server
        const merchantIds = selectedMerchants.map((m) => m._id).join(",");
        const params = new URLSearchParams();
        params.append("merchantIds", merchantIds);

        data = await get<any>(`/merchants/download?${params.toString()}`);
      } else {
        // Download filtered merchants
        if (selectedStatuses.length === 0) {
          setError("Please select at least one status");
          setIsDownloading(false);
          return;
        }

        if (selectedCheckoutTypes.length === 0) {
          setError("Please select at least one checkout type");
          setIsDownloading(false);
          return;
        }

        // Build query string with all filters
        const params = new URLSearchParams();
        params.append("statuses", selectedStatuses.join(","));
        params.append("checkoutTypes", selectedCheckoutTypes.join(","));

        const createdFromUtc = convertToUtcIso(createdAtFrom);
        const createdToUtc = convertToUtcIso(createdAtTo);
        const updatedFromUtc = convertToUtcIso(updatedAtFrom);
        const updatedToUtc = convertToUtcIso(updatedAtTo);

        if (createdFromUtc) params.append("createdAtFrom", createdFromUtc);
        if (createdToUtc) params.append("createdAtTo", createdToUtc);
        if (updatedFromUtc) params.append("updatedAtFrom", updatedFromUtc);
        if (updatedToUtc) params.append("updatedAtTo", updatedToUtc);

        // Add lastCouponCount filter
        if (lastCouponCountFilter !== "all") {
          params.append("lastCouponCountFilter", lastCouponCountFilter);
        }

        data = await get<any>(`/merchants/download?${params.toString()}`);
      }

      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .slice(0, -5);
      const filename = `agentic_merchants-${timestamp}.json`;

      // Create and trigger download
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setIsDownloading(false);
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err.message || "Failed to download merchants");
      setIsDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Download Merchants</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {selectedMerchants.length > 0 ? (
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k as "selected" | "filtered")}
            className="mb-3"
          >
            <Tab
              eventKey="selected"
              title={`Selected (${selectedMerchants.length})`}
            >
              <div className="mt-3">
                <p>
                  Download the {selectedMerchants.length} selected merchant
                  {selectedMerchants.length !== 1 ? "s" : ""}:
                </p>
                <div
                  className="border p-3 rounded"
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  <ul className="mb-0">
                    {selectedMerchants.map((merchant) => (
                      <li key={merchant._id}>{merchant.name}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Tab>
            <Tab eventKey="filtered" title="Advanced Filters">
              <div className="mt-3">
                <p className="mb-3">
                  Select which statuses you want to include in the download:
                </p>

                <Form.Group className="mb-3">
                  <div className="mb-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleSelectAll}
                      className="me-2"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div
                    className="border p-3 rounded"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {Object.values(MerchantStatusType).map((status) => (
                      <Form.Check
                        key={status}
                        type="checkbox"
                        id={`status-${status}`}
                        label={status}
                        checked={selectedStatuses.includes(status)}
                        onChange={() => handleStatusChange(status)}
                        className="mb-2"
                      />
                    ))}
                  </div>
                </Form.Group>

                <small className="text-muted d-block mb-3">
                  {selectedStatuses.length} status
                  {selectedStatuses.length !== 1 ? "es" : ""} selected
                </small>

                <hr />

                <p className="mb-3">
                  Select which checkout types you want to include in the
                  download:
                </p>

                <Form.Group className="mb-3">
                  <div className="mb-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleSelectAllCheckoutTypes}
                      className="me-2"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeselectAllCheckoutTypes}
                    >
                      Deselect All
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <div
                    className="border p-3 rounded"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {Object.values(checkoutTechnologyType).map(
                      (checkoutType) => (
                        <Form.Check
                          key={checkoutType}
                          type="checkbox"
                          id={`checkout-type-${checkoutType}`}
                          label={checkoutType}
                          checked={selectedCheckoutTypes.includes(checkoutType)}
                          onChange={() =>
                            handleCheckoutTypeChange(checkoutType)
                          }
                          className="mb-2"
                        />
                      )
                    )}
                  </div>
                </Form.Group>

                <small className="text-muted d-block mb-3">
                  {selectedCheckoutTypes.length} checkout type
                  {selectedCheckoutTypes.length !== 1 ? "s" : ""} selected
                </small>

                <hr />

                <p className="mb-3">
                  <strong>Coupon Count Filter</strong>
                </p>

                <Form.Group className="mb-3">
                  <div className="d-flex flex-column gap-2">
                    <Form.Check
                      type="radio"
                      id="lastCouponCount-all"
                      label="All merchants"
                      checked={lastCouponCountFilter === "all"}
                      onChange={() => setLastCouponCountFilter("all")}
                    />
                    <Form.Check
                      type="radio"
                      id="lastCouponCount-withCoupons"
                      label="Only merchants with coupons (lastCouponCount > 0)"
                      checked={lastCouponCountFilter === "withCoupons"}
                      onChange={() => setLastCouponCountFilter("withCoupons")}
                    />
                    <Form.Check
                      type="radio"
                      id="lastCouponCount-noCoupons"
                      label="Only merchants without coupons (lastCouponCount = 0)"
                      checked={lastCouponCountFilter === "noCoupons"}
                      onChange={() => setLastCouponCountFilter("noCoupons")}
                    />
                  </div>
                </Form.Group>

                <hr />

                <p className="mb-3">
                  <strong>Date Filters (Optional)</strong>
                </p>

                <Form.Group className="mb-3">
                  <Form.Label className="mb-2">
                    Quick Presets for Updated At
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setUpdatedInLastHours(24)}
                    >
                      Last 24 Hours
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setUpdatedInLastHours(72)}
                    >
                      Last 3 Days
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setUpdatedInLastHours(168)}
                    >
                      Last 7 Days
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={clearDateFilters}
                    >
                      Clear Dates
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Created At - From</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={createdAtFrom}
                    onChange={(e) => setCreatedAtFrom(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Created At - To</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={createdAtTo}
                    onChange={(e) => setCreatedAtTo(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Updated At - From</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={updatedAtFrom}
                    onChange={(e) => setUpdatedAtFrom(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Updated At - To</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    value={updatedAtTo}
                    onChange={(e) => setUpdatedAtTo(e.target.value)}
                  />
                </Form.Group>
              </div>
            </Tab>
          </Tabs>
        ) : (
          <>
            <p className="mb-3">
              Select which statuses you want to include in the download:
            </p>

            <Form.Group className="mb-3">
              <div className="mb-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleSelectAll}
                  className="me-2"
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDeselectAll}
                >
                  Deselect All
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <div
                className="border p-3 rounded"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {Object.values(MerchantStatusType).map((status) => (
                  <Form.Check
                    key={status}
                    type="checkbox"
                    id={`status-${status}`}
                    label={status}
                    checked={selectedStatuses.includes(status)}
                    onChange={() => handleStatusChange(status)}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>

            <small className="text-muted d-block mb-3">
              {selectedStatuses.length} status
              {selectedStatuses.length !== 1 ? "es" : ""} selected
            </small>

            <hr />

            <p className="mb-3">
              Select which checkout types you want to include in the download:
            </p>

            <Form.Group className="mb-3">
              <div className="mb-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleSelectAllCheckoutTypes}
                  className="me-2"
                >
                  Select All
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDeselectAllCheckoutTypes}
                >
                  Deselect All
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <div
                className="border p-3 rounded"
                style={{ maxHeight: "300px", overflowY: "auto" }}
              >
                {Object.values(checkoutTechnologyType).map((checkoutType) => (
                  <Form.Check
                    key={checkoutType}
                    type="checkbox"
                    id={`checkout-type-${checkoutType}`}
                    label={checkoutType}
                    checked={selectedCheckoutTypes.includes(checkoutType)}
                    onChange={() => handleCheckoutTypeChange(checkoutType)}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>

            <small className="text-muted d-block mb-3">
              {selectedCheckoutTypes.length} checkout type
              {selectedCheckoutTypes.length !== 1 ? "s" : ""} selected
            </small>

            <hr />

            <p className="mb-3">
              <strong>Coupon Count Filter</strong>
            </p>

            <Form.Group className="mb-3">
              <div className="d-flex flex-column gap-2">
                <Form.Check
                  type="radio"
                  id="lastCouponCount-all-notab"
                  label="All merchants"
                  checked={lastCouponCountFilter === "all"}
                  onChange={() => setLastCouponCountFilter("all")}
                />
                <Form.Check
                  type="radio"
                  id="lastCouponCount-withCoupons-notab"
                  label="Only merchants with coupons (lastCouponCount > 0)"
                  checked={lastCouponCountFilter === "withCoupons"}
                  onChange={() => setLastCouponCountFilter("withCoupons")}
                />
                <Form.Check
                  type="radio"
                  id="lastCouponCount-noCoupons-notab"
                  label="Only merchants without coupons (lastCouponCount = 0)"
                  checked={lastCouponCountFilter === "noCoupons"}
                  onChange={() => setLastCouponCountFilter("noCoupons")}
                />
              </div>
            </Form.Group>

            <hr />

            <p className="mb-3">
              <strong>Date Filters (Optional)</strong>
            </p>

            <Form.Group className="mb-3">
              <Form.Label className="mb-2">
                Quick Presets for Updated At
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setUpdatedInLastHours(24)}
                >
                  Last 24 Hours
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setUpdatedInLastHours(72)}
                >
                  Last 3 Days
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setUpdatedInLastHours(168)}
                >
                  Last 7 Days
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearDateFilters}
                >
                  Clear Dates
                </Button>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Created At - From</Form.Label>
              <Form.Control
                type="datetime-local"
                value={createdAtFrom}
                onChange={(e) => setCreatedAtFrom(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Created At - To</Form.Label>
              <Form.Control
                type="datetime-local"
                value={createdAtTo}
                onChange={(e) => setCreatedAtTo(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Updated At - From</Form.Label>
              <Form.Control
                type="datetime-local"
                value={updatedAtFrom}
                onChange={(e) => setUpdatedAtFrom(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Updated At - To</Form.Label>
              <Form.Control
                type="datetime-local"
                value={updatedAtTo}
                onChange={(e) => setUpdatedAtTo(e.target.value)}
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isDownloading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleDownload}
          disabled={
            isDownloading ||
            (activeTab === "selected"
              ? selectedMerchants.length === 0
              : selectedStatuses.length === 0 ||
                selectedCheckoutTypes.length === 0)
          }
        >
          {isDownloading ? "Downloading..." : "Download"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
